import { DOCUMENT } from "@angular/common";
import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostListener,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
} from "@angular/core";
import { Subscription } from "rxjs";

import { Asset } from "@hermes/api-model-asset";
import { Product } from "@hermes/api-model-product";
import { Context } from "@hermes/app-core";
import { stopEventPropagation } from "@hermes/utils-generic/helpers";
import { BreakpointService } from "@hermes/utils-generic/services/user-interface";

@Component({
    selector: "h-product-image-zoom",
    templateUrl: "./product-image-zoom.component.html",
    styleUrls: ["./product-image-zoom.component.scss"],
})
export class ProductImageZoomComponent implements OnInit, OnDestroy {
    /**
     * list of assets
     */
    @Input()
    public assets!: Asset[];

    /**
     * selected product
     */
    @Input()
    public product!: Product;

    @Input()
    public currentAssetUrl!: string;

    @Input()
    public backgroundWhite: boolean = false;

    /**
     * true if we're on zoom modal is open.
     */
    @Input()
    public zoomActive!: boolean;

    @Output()
    public zoomActiveChange = new EventEmitter<boolean>();

    public isMobile: boolean = false;

    public currentIndex: number = 0;

    public prefixTab: string = "zoom-tab";

    public thumbnailSize: number = 130;

    public readonly containerWithForInitialSCale: string = "100%";

    private subscription = new Subscription();

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private readonly context: Context,
        private changeDetector: ChangeDetectorRef,
        private breakpointService: BreakpointService,
        private renderer: Renderer2,
    ) {}

    @HostListener("document:keyup.escape", ["$event"])
    public onKeydownHandler(): void {
        this.closeModal();
    }

    public ngOnInit(): void {
        if (this.context && this.context.isInBrowserMode()) {
            if (this.assets) {
                this.product.assets = this.assets;
                this.changeDetector.detectChanges();
            }
            this.subscription.add(
                this.breakpointService.isMobile$.subscribe(
                    (isMobile: boolean) => {
                        this.isMobile = isMobile;
                        this.changeDetector.detectChanges();
                    },
                ),
            );
            this.renderer.setStyle(this.document.body, "overflowY", "hidden");
            this.renderer.setStyle(this.document.body, "position", "fixed");
            this.calculateCurrentIndex();
        }
    }

    public calculateCurrentIndex(): void {
        if (this.assets) {
            const index = this.assets.findIndex(
                (asset) => asset.url === this.currentAssetUrl,
            );
            this.currentIndex = index > 0 ? index : 0;
        }
    }

    /**
     * close modal
     */
    public closeModal(event?: MouseEvent): void {
        event?.stopPropagation();
        if (this.context.isInBrowserMode()) {
            this.renderer.removeStyle(this.document.body, "overflowY");
            this.renderer.removeStyle(this.document.body, "position");
        }
        this.zoomActive = false;

        // currently the output is only used by personalization configurator
        this.zoomActiveChange.emit(this.zoomActive);
    }

    /**
     * update current index when index change from gallery component
     */
    public handleIndexChange(index: number): void {
        this.currentIndex = index;
        this.changeDetector.detectChanges();
    }

    /**
     * Prevents the scroll to go to the page.
     */
    public stopPropagation(event: Event): void {
        stopEventPropagation(event);
        event.stopImmediatePropagation();
    }

    /**
     * Prevents zoom persistence
     */
    public ngOnDestroy(): void {
        this.closeModal();
        this.subscription.unsubscribe();
    }
}
